import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import seedrandom from "seedrandom";
import { useAudioContext } from "../contexts/audio";
import { DropEntry, VariantFile, VariantItem } from "../dropTypes";
import { unmute } from "../lib/unmute";
import { randomInt } from "../utils/math";

export type AudioStates = "idle" | "paused" | "playing" | "ended";

export const useDropContent = (
  drop: DropEntry
): {
  musicContent: VariantFile[]; // Only Music
  videoContent: VariantItem[]; // Only Videos
  extraContent: VariantItem[]; // Everything but Music (Images + Videos + ...)
  mainPreview: VariantFile;
} => {
  const rand = seedrandom(drop.id);
  // The Items array holds fileGroupIds which correspond with the fileGroupId in the Files array. E.g:
  // Items: [{name: "An album", fileGroupIds: ["123", "456"]}]
  // Files: [{fileGroupId: "123", name: "A song"}, {fileGroupId: "456", name: "Another song"}]
  const content = useMemo(() => {
    let randomCount = 0;
    const metadata = drop?.variants?.[0]?.collectionMetadata;
    const idCache = [];
    const uniquePreviewID = () => {
      let id = randomInt(1, 20, rand.quick());
      if (idCache.includes(id) && randomCount++ < 20) {
        id = uniquePreviewID();
      }
      idCache.push(id);
      return id;
    };

    if (metadata) {
      const musicContent: VariantFile[] = metadata.items
        // Filter out rare/legendary items
        .filter(
          (item) =>
            item.clientData?.contentTags?.[0] === "music" &&
            !item.clientData?.contentTags?.includes("rare") &&
            !item.clientData?.contentTags?.includes("legendary")
        )
        .map((item) =>
          metadata.files.filter(
            (file) =>
              item.fileGroupIds.includes(file.fileGroupId) && file.preview
          )
        )
        .filter(Boolean)
        .reduce((acc, val) => acc.concat(val), []);

      const extraContent: VariantItem[] = metadata.items
        .filter((item) => item.clientData?.contentTags?.[0] !== "music")
        .map((item) => {
          const preview = metadata.files
            .filter(
              (file) =>
                item.fileGroupIds.includes(file.fileGroupId) &&
                (file.preview || file.thumbnail)
            )
            .map((file) =>
              file.thumbnail
                ? {
                    uri: file.thumbnail?.uri,
                    mime: file.thumbnail?.mime,
                  }
                : {
                    uri: file.preview?.uri,
                    mime: file.preview?.mime,
                  }
            );
          if (preview.length < 1) {
            preview.push({
              uri: `/secureThumbs/${uniquePreviewID()}.jpg`,
              mime: "image/jpeg",
            });
          }
          return {
            ...item,
            preview,
          };
        });

      const videoContent: VariantItem[] = metadata.items
        .filter(
          (item) =>
            item.type === "single-video" || item.type === "multiple-video"
        )
        .map((item) => {
          const preview = metadata.files
            .filter(
              (file) =>
                item.fileGroupIds.includes(file.fileGroupId) && file.thumbnail
            )
            .map((file) => ({
              uri: file.thumbnail?.uri,
              mime: file.thumbnail?.mime,
            }));
          if (preview.length < 1) {
            preview.push({
              uri: `/secureThumbs/${uniquePreviewID()}.jpg`,
              mime: "image/jpeg",
            });
          }
          return {
            ...item,
            preview,
          };
        });

      const mainPreviewFileGroupIds = metadata.items.find(
        (item) => !item.encrypted
      )?.fileGroupIds;

      let mainPreviewFile: VariantFile;

      if (mainPreviewFileGroupIds) {
        mainPreviewFile = metadata.files.find((file) =>
          mainPreviewFileGroupIds.includes(file.fileGroupId)
        );
      } else if (musicContent.length) {
        mainPreviewFile = musicContent[0];
      } else {
        mainPreviewFile = metadata.files.find((file) =>
          file.mime.includes("image")
        );
      }

      return {
        musicContent,
        videoContent,
        extraContent,
        mainPreviewFile,
      };
    }
  }, [drop]);

  return {
    musicContent: content?.musicContent ?? [],
    videoContent: content?.videoContent ?? [],
    extraContent: content?.extraContent ?? [],
    mainPreview: content?.mainPreviewFile ?? undefined,
  };
};

export const useAudio = (urls: string[]) => {
  const [state, setState] = useState<PlaybackState>("idle");
  const [currentTrack, setCurrentTrack] = useState(-1);
  const [tracksLoaded, setLoaded] = useState<boolean[]>(() =>
    urls.map(() => false)
  );
  const audioContext = useAudioContext();

  const playlistRef = useRef<AudioContextPlayer>();

  useEffect(() => {
    if (audioContext && urls.length) {
      unmute(audioContext);
      const audioPlayer = new AudioContextPlayer(audioContext, urls);
      audioPlayer.onChange((s, i, t) => {
        setState(s);
        setCurrentTrack(i);
        setLoaded(t);
      });
      playlistRef.current = audioPlayer;

      return () => {
        playlistRef.current = undefined;
        audioPlayer.destory();
      };
    }
  }, [audioContext, urls]);

  const stopPlayback = useCallback(() => {
    const playlist = playlistRef.current;

    if (!playlist) {
      return;
    }

    if (playlist.isPlaying) playlist.pause();
  }, [playlistRef]);

  const togglePlayback = useCallback(
    (startIndex: number = -1) => {
      const playlist = playlistRef.current;

      if (!playlist) {
        return;
      }

      startIndex =
        startIndex >= 0
          ? startIndex
          : playlist.currentTrackIndex >= 0
          ? playlist.currentTrackIndex
          : 0;

      if (playlist.isPlaying && playlist.currentTrackIndex === startIndex) {
        playlist.pause();
      } else {
        const event = new Event("audioPlaying", {});
        document.dispatchEvent(event);
        playlist.playTrack(startIndex);
      }
    },
    [playlistRef]
  );

  return {
    state,
    currentTrack,
    togglePlayback,
    tracksLoaded,
    stopPlayback,
  };
};

const CROSSFADE_DURATION = 5; // seconds
export type PlaybackState = "idle" | "paused" | "playing" | "ended";
export type onChangeCallback = (
  state: PlaybackState,
  currentTrackIndex: number,
  tracksLoaded: boolean[]
) => void;

class AudioContextPlayer {
  private _isPlaying: boolean = false;
  private _state: PlaybackState = "idle";
  private _currentTrackIndex: number = -1;
  private _audioBuffers: AudioBuffer[] = [];
  private _audioBuffersPromises: Promise<AudioBuffer | void>[];
  private _sources: AudioBufferSourceNode[] = [];
  private _listeners: onChangeCallback[] = [];
  private _startTime: number = 0;
  private _pauseTime: number = 0;

  constructor(private _audioContext: AudioContext, private _urls: string[]) {
    this._audioBuffersPromises = _urls.map(() => undefined);
  }

  private async loadAudioBuffer(index: number) {
    const url = this._urls[index];

    if (url) {
      if (!this._audioBuffersPromises[index]) {
        const promise = loadAudioBuffer(this._audioContext, url).then((b) => {
          this._audioBuffers[index] = b;
          return b;
        });
        this._audioBuffersPromises[index] = promise;
      }

      return this._audioBuffersPromises[index] as Promise<AudioBuffer>;
    }

    throw new Error("No url found for index: " + index);
  }

  private emit() {
    this._listeners.forEach((l) =>
      l(
        this._state,
        this._currentTrackIndex,
        this._urls.map((_, i) => this._audioBuffers[i] !== undefined)
      )
    );
  }

  public onChange(callback: onChangeCallback) {
    this._listeners.push(callback);

    return () => {
      this._listeners = this._listeners.filter((l) => l !== callback);
    };
  }

  public async playTrack(index) {
    const previousTrack = this._currentTrackIndex;
    const currentTrack = index;
    const offset = previousTrack === currentTrack ? this._pauseTime : 0;

    // Ensures audio will play on Safari iOS
    // See: https://stackoverflow.com/questions/57510426/cannot-resume-audiocontext-in-safari
    this._audioContext.resume();

    if (this._isPlaying && this._sources[previousTrack]) {
      this._sources[previousTrack].onended = null;
      this._sources[previousTrack].stop(this._audioContext.currentTime);
    }

    this._isPlaying = true;
    this._state = "playing";
    this._currentTrackIndex = currentTrack;
    this._startTime = this._audioContext.currentTime;
    this.emit();

    // Wait for track buffer to load
    const buffer = await this.loadAudioBuffer(currentTrack);
    // Start preloading next track
    if (this._urls[currentTrack + 1]) {
      this.loadAudioBuffer(currentTrack + 1);
    }
    const source = createBufferSource(
      this._audioContext,
      buffer,
      this._startTime,
      CROSSFADE_DURATION
    );
    source.onended = () => this.playNextTrack();
    this._sources[index] = source;
    source.start(this._startTime, offset);
    this.emit();
  }

  public playNextTrack() {
    if (this._currentTrackIndex + 1 < this._audioBuffers.length) {
      this._pauseTime = 0;
      this.playTrack(this._currentTrackIndex + 1);
    } else {
      this._state = "ended";
      this._isPlaying = false;
      this.emit();
    }
  }

  public pause() {
    if (this._isPlaying && this._sources[this.currentTrackIndex]) {
      this._sources[this.currentTrackIndex].onended = null;
      this._sources[this.currentTrackIndex].stop(
        this._audioContext.currentTime
      );
      this._pauseTime += this._audioContext.currentTime - this._startTime;
      this._isPlaying = false;
      this._state = "paused";
      this.emit();
    }
  }

  public destory() {
    this._sources.forEach((s) => {
      s.onended = null;
      try {
        s.stop();
      } catch (e) {
        // Ignore
      }
    });
    this._listeners = [];
    this._sources = [];
    this._audioBuffers = [];
    this._audioBuffersPromises = [];
  }

  get isPlaying() {
    return this._isPlaying;
  }

  get currentTrackIndex() {
    return this._currentTrackIndex;
  }
}

async function loadAudioBuffer(
  audioContext: AudioContext,
  url: string
): Promise<AudioBuffer> {
  const response = await fetch(url);
  const arrayBuffer = await response.arrayBuffer();
  return audioContext.decodeAudioData(arrayBuffer);
}

function createBufferSource(
  audioContext: AudioContext,
  audioBuffer: AudioBuffer,
  startTime: number,
  fadeDuration: number
): AudioBufferSourceNode {
  const gainNode = audioContext.createGain();
  gainNode.gain.setValueAtTime(0, startTime);
  gainNode.gain.linearRampToValueAtTime(1, startTime + fadeDuration);
  gainNode.connect(audioContext.destination);

  const stopTime = startTime + audioBuffer.duration;
  const fadeOutStartTime = stopTime - fadeDuration;
  gainNode.gain.setValueAtTime(1, fadeOutStartTime);
  gainNode.gain.linearRampToValueAtTime(0, stopTime);

  const source = audioContext.createBufferSource();
  source.buffer = audioBuffer;
  source.connect(gainNode);

  return source;
}
